import React from 'react';

/* Comprehensive user guide for the Portfolio Trading app. Static content — update this
   file when workflows change (the platform format cards mirror trade_sheets.py exactly).
   Rendered via the 📖 sidebar entry. */

const S = ({ id, title, children }) => (
  <section className="guide-section" id={id}>
    <h3>{title}</h3>
    {children}
  </section>
);

/* ── Trade-sheet graphics helpers ── */

const Flow = ({ steps }) => (
  <div className="gsheet-flow">
    {steps.map(([title, sub], i) => (
      <React.Fragment key={title}>
        {i > 0 && <span className="gsheet-arrow">→</span>}
        <div className="gsheet-step">
          <div className="gsheet-step-t">{title}</div>
          <div className="gsheet-step-s">{sub}</div>
        </div>
      </React.Fragment>
    ))}
  </div>
);

/* Mock file window: title bar = filename, body = the exact layout with header lines
   highlighted. line = [text, kind] where kind ∈ hdr|row|cash|dim. */
const FileMock = ({ fname, lines, note }) => (
  <div className="gsheet-file">
    <div className="gsheet-fname" title="Filename pattern">{fname}</div>
    <pre>
      {lines.map(([txt, kind], i) => (
        <div key={i} className={`gsheet-line ${kind || 'row'}`}>{txt}</div>
      ))}
    </pre>
    {note && <div className="gsheet-note">{note}</div>}
  </div>
);

const PlatformCard = ({ name, badge, chips, mock, rules }) => (
  <div className={`gsheet-card${badge === 'parked' ? ' parked' : ''}`}>
    <div className="gsheet-card-head">
      <strong>{name}</strong>
      {badge && <span className={`gsheet-badge ${badge}`}>{badge}</span>}
      <span className="gsheet-chips">
        {chips.map(([k, v]) => (
          <span key={k} className="gsheet-chip"><em>{k}</em> {v}</span>
        ))}
      </span>
    </div>
    {mock}
    {rules && <ul className="gsheet-rules">{rules.map((r, i) => <li key={i}>{r}</li>)}</ul>}
  </div>
);

export default function UserGuide() {
  return (
    <div className="guide">
      <header className="trading-header"><div><h2>User guide — Portfolio Trading</h2>
        <div className="trading-header-sub"><span className="trading-muted">
          The system of record for target weights: author → approve → record sub exceptions → generate trade sheets.
        </span></div></div></header>

      <nav className="guide-toc">
        {[['concepts', 'Core concepts'], ['grid', 'The suite grid'], ['tilts', 'SAA tilts'],
          ['compliance', 'Compliance'], ['mandate', 'Mandate checks'], ['governance', 'Approvals & versions'], ['subs', 'Sub exceptions'],
          ['audit', 'Audit & comparison'], ['upload', 'Bulk upload'], ['sheets', 'Trade sheets'],
          ['platforms', 'Platform formats'],
          ['funddata', 'Registered instruments & costs'], ['report', 'Charts & tables'], ['faq', 'Gotchas & FAQ']].map(([id, label]) => (
            <a key={id} href={`#${id}`}>{label}</a>
          ))}
      </nav>

      <S id="concepts" title="Core concepts">
        <ul>
          <li><strong>Master vs sub-portfolio.</strong> A <em>master</em> (blender code, e.g. DS70) is the strategy —
            one target set of fund weights. <em>Sub-portfolios</em> (portfolio codes) are the platform vehicles mapped to
            it. A sub's target is <em>defined</em> as master ∘ its recorded exceptions — there is nothing to sync and
            nothing can drift silently.</li>
          <li><strong>This app is the source of truth for targets.</strong> Shadow_Data (the platform ledger) is the
            <em> post-execution</em> record — it never supplies target weights. It appears only as reference data
            (fund fees, names) and, later, for trade-sheet reconciliation.</li>
          <li><strong>Current = last approved.</strong> The Current column shows the standing, maker-checked
            instruction. Saved drafts load into <em>Proposed</em>, so Δ always shows what's awaiting approval.</li>
          <li><strong>Platform Cash.</strong> The model holds one neutral cash line (<code>PLATFORM_CASH</code>).
            Each platform's own token (TP Cash, AUD, CASH…) is applied only when trade sheets are generated.</li>
          <li><strong>Nothing is deleted.</strong> Versions supersede, instruments retire, overrides deactivate —
            history persists for audit.</li>
        </ul>
      </S>

      <S id="grid" title="The suite grid (main view)">
        <ul>
          <li><strong>Columns per portfolio:</strong> Current (fixed, approved) | Δ (edit = adds to current) |
            Proposed (edit = absolute). Editing either solves the other. Header tags: green
            <em> approved vN</em> = standing instruction; gold <em>draft vN</em> = awaiting approval (loaded into
            Proposed); <em>⇩ import Shadow</em> = one-time seed for a never-authored strategy.</li>
          <li><strong>Keyboard:</strong> ↑ ↓ ← → move between cells like a spreadsheet; Enter moves down; values
            auto-select on landing. <strong>Paste from Excel:</strong> copy a column of weights, click a cell,
            paste — values fill downward (into Δ they add to current; into Proposed they're absolute).</li>
          <li><strong>Portfolios picker</strong> chooses which portfolios you're trading on (remembered per suite).
            <strong> Group by</strong> switches the row grouping between Asset class group / Asset class /
            Sub-asset class (AP List taxonomy).</li>
          <li><strong>+ Add fund</strong> searches the screener; <strong>Register instrument</strong> (inside the
            panel) adds codes not in the AP List (sub-SMAs, special lines) — metadata incl. platform is required.
            A fund row can only be deleted (✕ on hover) when it has no weight on either side anywhere.</li>
          <li><strong>⧉ Copy from…</strong> copies another portfolio's allocation into Proposed (full replace;
            cash token remapped to the target's own). <strong>↺</strong> reverts a column's edits;
            the amber <em>unsaved edits</em> chip guards navigation.</li>
          <li><strong>MER / Rebate / Net MER:</strong> manual fund-level values (MER seeds from Manager Fee).
            Saved on blur, global across suites, persistent. Weighted totals appear in the summary rows.</li>
          <li><strong>Version note</strong> (header input) is stored on the next Save draft / Submit and shows in
            the audit trail. <strong>⇓ Excel</strong> downloads the grid; <strong>⇈ Submit all</strong> submits
            every portfolio in view (skips hard-stopped ones).</li>
        </ul>
      </S>

      <S id="tilts" title="SAA tilts">
        <ul>
          <li>Bars = weight − SAA neutral per asset class (colour = asset class; right = overweight).</li>
          <li><strong>Level</strong>: asset class group / asset class / sub-asset class (neutrals stored per SAC,
            rolled up via the AP List). <strong>Basis</strong>: Current vs SAA, Proposed vs SAA (default), or
            Δ change (proposed − current; works even with no SAA on record).</li>
          <li><strong>FY selector</strong> re-points neutrals to another SAA vintage without touching your edits.</li>
        </ul>
      </S>

      <S id="compliance" title="Pre-trade compliance">
        <ul>
          <li>Rules evaluate <em>live</em> against proposed weights: the badge row shows ✓ clear / ⚠ flags /
            ✗ blocks per portfolio; details + reason inputs sit below the grid.</li>
          <li><strong>Hard-stops block submission; soft flags need a recorded reason.</strong></li>
          <li>Rules are managed in ⚖ Compliance rules and scope three ways — global → master → sub-portfolio —
            with the most specific winning. Rule types include sum-to-100, growth band, tilt limits,
            SAC bucket bands (e.g. EQ_AU+EQ_WORLD+EQ_EM within 25–50%), turnover, weighted MER, currency band,
            holdings count and more.</li>
        </ul>
      </S>

      <S id="mandate" title="Mandate checks">
        <ul>
          <li>☑ Mandate checks reproduces the Portfolio Viewer "Mandate Check" sheet per suite, live: each asset-class
            row shows OK/CHECK plus the exposure, with the <strong>Mandate Range</strong> row underneath. The ranges
            are the sub-asset-class bucket rules in ⚖ Compliance rules — the same engine as the pre-trade gate, so a
            CHECK here is a block there. The rules are the source of truth; the sheet's typed ranges are not used.</li>
          <li>Rows: Global Alpha, Australian Equities, International Equities, Real Estate, Infrastructure, Cash,
            Australian Fixed Interest and Global Fixed Interest (the fixed-interest rows follow the Australian/global
            split the rules enforce, not the sheet's government/corporate split). The top table is <strong>strictly what
            the portal enforces</strong>: the sheet rows with a rule on at least one portfolio in the suite, then every
            other enforced rule (other bucket definitions, sum-to-100, growth band, minimum cash). Nothing unenforced
            appears in it.</li>
          <li>The separate <strong>Not enforced in the portal</strong> table underneath holds everything else: any sheet
            row with no rule in the suite (exposure only), the manual <strong>Max Issuer (35%)</strong> tick and the
            standing <strong>Illiquid Assets &lt; 15%</strong> OK.</li>
          <li><strong>Basis</strong>: Approved (the standing instruction) or Proposed (the live draft/pending set where
            one exists, else approved). Portfolios without any target are hidden unless toggled on.</li>
          <li><strong>Max Issuer (35%)</strong> is a manual tick per portfolio — no issuer data exists on fund records.
            The tick is keyed to the target set shown, so it carries through that set's approval and clears when a
            newer set is approved. Ticked cells show OK and the date.</li>
          <li><strong>Illiquid Assets &lt; 15%</strong> is a standing OK: all portfolios are daily liquid. It is not
            computed and will not detect a non-daily-liquid fund added later.</li>
          <li>The sheet's MER and Non-AUD lines are not shown — there is no mandate for either.</li>
          <li>⧉ Copy table pastes the block (with OK/CHECK colouring) into Excel, Word or Outlook.
            ▣ Copy for PowerPoint puts a picture of the block on the clipboard sized for a 16:9 slide
            (12.3in wide at 192 dpi, Calibre, brand colours) — paste straight onto a slide. The picture carries the
            enforced table only; the Not enforced table is left out (Copy table includes both). The button is
            held, with a red note naming the portfolios outstanding, until Max Issuer (35%) is ticked for every
            portfolio in the block — the tick itself lives in the Not enforced table. Browsers that cannot copy
            images download the PNG instead.</li>
        </ul>
      </S>

      <S id="governance" title="Approvals & version lifecycle">
        <p><code>draft → pending approval → approved → superseded</code></p>
        <ul>
          <li><strong>Save draft</strong> stores a version without submitting. <strong>Submit</strong> runs the
            compliance gate and sends it to ⧉ Pending approvals.</li>
          <li>A checker (≠ author in production; self-approval is temporarily enabled for testing) reviews the
            compliance snapshot + lines and <strong>Approves</strong> (becomes Current, prior approved superseded)
            or <strong>Rejects</strong> (back to author). Authors can <strong>Withdraw</strong> their own pending
            submission. Batch approve/reject via the checkboxes.</li>
          <li><strong>Withdraw approval</strong> (audit trail → click a dot → modal) un-approves a set and
            automatically reinstates the previous superseded version. <strong>Restore as draft</strong> copies any
            historical version into a new draft — the rollback path.</li>
        </ul>
      </S>

      <S id="subs" title="Sub exceptions (drill view)">
        <ul>
          <li>Click a portfolio header to open the drill: the model column + every mapped vehicle showing its
            <em> resolved target</em>. With no exceptions, every sub is identical to the master by construction.</li>
          <li><strong>Edit a sub's cell to record an exception</strong> — a commit bar collects the required reason;
            0 = exclude, other values = reweight. Gold cells are recorded exceptions (reason on hover);
            ◆ n counts them per vehicle; the exceptions panel lists and deletes them (revert to model).</li>
          <li>Substitutions/additions and unit-class equivalences (incl. cash tokens) are handled the same way and
            never read as divergence.</li>
        </ul>
      </S>

      <S id="audit" title="Audit trail, cascade & comparison">
        <ul>
          <li><strong>Timeline:</strong> every version ever created — dots per strategy on a time axis (colour =
            status). Click a dot/row for the stored weights + actions; click the <em>code label</em> for the
            portfolio registry details.</li>
          <li><strong>Evolution:</strong> one strategy across its versions — stacked allocation bars + a fund×version
            change matrix (green/red = moves), with restore links.</li>
          <li><strong>Compare:</strong> any two versions/portfolios — overlap %, weight moved, asset-class and
            fund-level deltas.</li>
          <li><strong>Bulk compare:</strong> many portfolios at once (add whole suites, remove groups), chart or
            table, at any taxonomy level; expand a master to see its subs' resolved targets, with "divergent only"
            hiding subs that match the model.</li>
          <li><strong>⛓ Cascade map:</strong> master → platform → subs tree; gold subs carry exceptions
            (click to read them), green = follows the model exactly.</li>
        </ul>
      </S>

      <S id="upload" title="Bulk upload">
        <ul>
          <li>Spreadsheet: APIR column + one column per blender code = a draft per filled column. Download the
            template (all codes, current fund list). Blank columns are left untouched; cash tokens fold into
            Platform Cash; fractions auto-scale ×100; everything skipped/unknown is reported.</li>
        </ul>
      </S>

      <S id="sheets" title="Trade sheets">
        <ul>
          <li>Generates per-platform order files from <strong>approved targets only</strong>, resolved per sub,
            stamped with the export day. Formats replicate each platform exactly — see
            <a href="#platforms"> Platform formats</a> below for every file spec, with examples.</li>
          <li><strong>Preview (no files)</strong> shows the full report without producing anything. Generation
            excludes geared (&gt;100%) vehicles, reports unroutable codes, and flags registered instruments emitted
            onto the wrong platform. The ZIP mirrors the Dropbox folder structure and includes the report CSV;
            every run is audited.</li>
        </ul>
      </S>

      <S id="platforms" title="Platform trade-sheet formats — how each file is generated">
        <p>Every run follows the same pipeline; only the final rendering differs per platform:</p>
        <Flow steps={[
          ['1 · Approved target', 'last approved version per strategy — drafts never trade'],
          ['2 · Resolve per sub', 'master ∘ recorded exceptions ∘ platform unit-class map'],
          ['3 · Route to platform', 'sheet meta → Shadow platform → declared platform (mapping)'],
          ['4 · Render format', 'one file per vehicle, or one consolidated file'],
          ['5 · ZIP + report', 'Dropbox folder structure + Generation_Report CSV'],
        ]} />
        <p><strong>Rules shared by every platform</strong> (before any format is applied):</p>
        <ul>
          <li><strong>%Trade basis:</strong> new approved resolved − previously executed (superseded) resolved.
            <strong> Sell-outs are explicit</strong> — a fund held in the previous target but not the new one is
            emitted at weight 0. A fund never held on either side is omitted entirely.</li>
          <li><strong>Platform Cash materialises as each platform's exact token</strong> (see the cards — the BT
            family alone has three different spellings). Netwealth omits cash altogether.</li>
          <li><strong>Unit-class mapping applies before rendering:</strong> a sub on a mapped platform emits the
            platform's own APIR wherever the model holds the mapped code — no manual exception needed.</li>
          <li><strong>Geared vehicles (total &gt; 100.5%) are excluded</strong> and reported. Vehicles with no
            resolvable platform are reported <em>unroutable</em>. Registered instruments emitted onto a platform
            they aren't valid for are flagged as warnings.</li>
          <li><strong>Export date = the day of export</strong> (not the effective date), stamped into every
            filename. Every run is stored with its report for audit.</li>
        </ul>

        <div className="gsheet-cards">
          <PlatformCard name="BT (SMA)" badge="per vehicle"
            chips={[['folder', 'BT/update'], ['cash', 'MP Cash'], ['type', 'CSV']]}
            mock={<FileMock fname="DRUS20070_003_20260902_Target Weight.csv"
              lines={[
                ['Model Portfolio Code,DRUS20070,', 'hdr'],
                ['Model Portfolio Name,Drummond Strategic 70 Portfolio,', 'hdr'],
                ['Add Your Commentary,,', 'hdr'],
                [',,', 'hdr'],
                ['Asset,% Target Weight,% Trade', 'hdr'],
                ['BGL0034AU,10.5,2', 'row'],
                ['SOL0001AU,8,', 'row'],
                ['ETL0018AU,0,-4', 'dim'],
                ['MP Cash,1.5,2', 'cash'],
              ]}
              note="grey row = explicit sell-out · blank % Trade = unchanged (|Δ| < 0.005) · bare numbers (3 not 3.0)" />}
            rules={[
              <>Tailored/partner SMAs (Sep 2026 format): <strong>Model Portfolio Code / Name</strong> headers, an <code>Add Your Commentary</code> row + blank row, then <code>Asset,% Target Weight,% Trade</code>. Cash trades as <strong>MP Cash</strong>.</>,
              <>Filename carries the vehicle's <strong>fixed numeric label</strong> (e.g. <code>_003</code>) from its seeded sheet meta.</>,
            ]} />

          <PlatformCard name="BT (MDA)" badge="per vehicle"
            chips={[['folder', 'BT/update'], ['cash', 'TP Cash'], ['type', 'CSV']]}
            mock={<FileMock fname="DSP70_20260902_Target Weight.csv"
              lines={[
                ['Model Identifier,DSP70,', 'hdr'],
                ['Model Name,Drummond Strategic Portfolio 70,', 'hdr'],
                ['Asset,% Allocation,%Trade', 'hdr'],
                ['BGL0034AU,10.5,2', 'row'],
                ['SOL0001AU,8,', 'row'],
                ['TP Cash,1.5,2', 'cash'],
              ]}
              note="the wholesale house models only (DDP + DSP suite), routed via sheet platform 'BT MDA' — same BT/ folder" />}
            rules={[
              <>Keeps the original layout with the spaced <code>% Allocation</code> header and <strong>TP Cash</strong>; bare numbers like the SMA files.</>,
            ]} />

          <PlatformCard name="Philo & FLP" badge="per vehicle"
            chips={[['folder', 'Philo/update · FLP/update'], ['cash', 'TPCASH · TP CASH'], ['type', 'CSV']]}
            mock={<FileMock fname="DS70PHI_20260714_Target Weight.csv"
              lines={[
                ['Model Identifier,DS70PHI,', 'hdr'],
                ['Model Name,Drummond Strategic 70,', 'hdr'],
                ['Asset,%Allocation,%Trade', 'hdr'],
                ['BGL0034AU,10.5,2.0', 'row'],
                ['TPCASH,1.5,-2.0', 'cash'],
              ]}
              note="same BT-family layout, but no space in '%Allocation' — and each has its own cash token: Philo TPCASH, FLP TP CASH" />}
            rules={[
              <>Both run on Panorama but export to <strong>their own folders</strong> with their own cash spellings — routing comes from the vehicle's sheet meta, not the Shadow platform label.</>,
            ]} />

          <PlatformCard name="AMP" badge="consolidated"
            chips={[['folder', 'AMP/ (root)'], ['cash', 'CASHACCT'], ['type', 'one bulk CSV for all models']]}
            mock={<FileMock fname="Bulk upload profiles template.csv"
              lines={[
                ['Model ID,Asset Code,% Weighting', 'hdr'],
                ['DS30AMP,VAN0002,12.0', 'row'],
                ['DS30AMP,CASHACCT,2.0', 'cash'],
                ['DS70AMP,BGL0034,10.5', 'row'],
                ['DS70AMP,CASHACCT,1.5', 'cash'],
              ]}
              note="APIR codes are stripped of their trailing 'AU' (9-character codes only)" />}
            rules={[
              <>Every AMP model stacked in <strong>one bulk file</strong> (Sep 2026). The filename is AMP's fixed template name — <strong>no date stamp</strong>. No %Trade column — weights only.</>,
            ]} />

          <PlatformCard name="HUB24" badge="consolidated"
            chips={[['folder', 'HUB/ (root)'], ['cash', 'CASH'], ['type', 'one MPWeights CSV for all models']]}
            mock={<FileMock fname="Drummond Capital Partners Pty Limited_MPWeights_14_07_2026.csv"
              lines={[
                ['PortfolioCode,PortfolioName,ProductCode,ProductName,Weight,OverrideWeight,OverrideLimitPrice,SubstituteCode,SubstituteDescription,SubstituteWeight', 'hdr'],
                ['DS70HUB,Drummond Strategic 70 Portfolio,BGL0034AU,iShares Australian Equity Index Fund,0.10500000,,,,,', 'row'],
                ['DS70HUB,Drummond Strategic 70 Portfolio,CASH,CASH,0.01500000,,,,,', 'cash'],
              ]}
              note="weights are FRACTIONS to 8dp (10.5% → 0.10500000) — the only platform that wants them" />}
            rules={[
              <>Every HUB model in <strong>one MPWeights file</strong> (Sep 2026) with portfolio and fund <strong>display names</strong>; the five override/substitute columns stay blank. Date in the filename is <strong>DD_MM_YYYY</strong>.</>,
            ]} />

          <PlatformCard name="Expand" badge="consolidated"
            chips={[['folder', 'Expand/ (root)'], ['cash', 'cash (lowercase)'], ['type', 'one bulk CSV for all models']]}
            mock={<FileMock fname="All Models - 2026-09-02.csv"
              lines={[
                ['ISMADRU01,SMA Drummond Strategic 30,BGL0034AU,2,Floating', 'row'],
                ['ISMADRU01,SMA Drummond Strategic 30,cash,1,Floating', 'cash'],
                ['ISMADRU02,SMA Drummond Strategic 50,SOL0001AU,5,Floating', 'row'],
              ]}
              note="still headerless — every Expand model stacked in ONE file (Sep 2026), bare-number weights" />}
            rules={[
              <>Each row is <code>code, display name (from Shadow), asset, weight, Floating</code> — the last column is a constant. Cash token stays <strong>lowercase</strong> <code>cash</code>.</>,
            ]} />

          <PlatformCard name="Macquarie" badge="per vehicle"
            chips={[['folder', 'Macquarie/update'], ['cash', 'AUD'], ['type', 'CSV']]}
            mock={<FileMock fname={'DS70MAC____________20260714__________.csv'}
              lines={[
                ['Update Model Weight,', 'hdr'],
                ['Security Name,Weight (2dp)', 'hdr'],
                ['BGL0034AU,10.5', 'row'],
                ['SOL0001AU,8.0', 'row'],
                ['AUD,1.5', 'cash'],
              ]}
              note="weights rounded to 2 decimal places; the padded underscores in the filename are required" />}
            rules={[
              <>Cash trades as the currency line <code>AUD</code>.</>,
            ]} />

          <PlatformCard name="Netwealth" badge="consolidated"
            chips={[['folder', 'Netwealth/ (root)'], ['cash', 'omitted'], ['type', 'one CSV for all models']]}
            mock={<FileMock fname="ManagedModelExport_14_07_2026.csv"
              lines={[
                ['Model Code,Asset Code,Floating Allocation', 'hdr'],
                ['DS30NW,VAN0002AU,12.0', 'row'],
                ['DS30NW,BGL0034AU,6.0', 'row'],
                ['DS70NW,BGL0034AU,10.5', 'row'],
                ['DS70NW,SOL0001AU,8.0', 'row'],
                ['DS70VW,DS70NW,100.0', 'link'],
              ]}
              note="every Netwealth model in ONE file — NO cash rows (the platform plugs cash implicitly); a linked child model is ONE row pointing at its parent" />}
            rules={[
              <><strong>Cash is deliberately omitted</strong> — Netwealth treats the unallocated remainder as cash, so emitting a token would double-count it.</>,
              <><strong>Linked (parent / child) models</strong> — a vehicle whose sheet meta records a <em>parent model</em> (the Verse <em>DS …</em> models follow the Drummond Strategic models) emits a single row <code>child,parent,100.0</code> instead of its holdings: Netwealth holds the child as a 100% position in the parent, so listing the look-through funds would double them up. Guards: Netwealth-only; the parent must be active, on Netwealth and mapped to the same strategy; no chains — anything else is reported <em>unroutable</em>. Recorded exceptions on the child are flagged in the report, never expressed.</>,
            ]} />

          <PlatformCard name="Mason Stevens" badge="consolidated"
            chips={[['folder', 'Mason Stevens/ (root)'], ['cash', 'AUD'], ['type', 'one XLSX, sheet per model']]}
            mock={<div className="gsheet-file">
              <div className="gsheet-fname">Mason_Stevens_Trade_Changes_14_07_2026.xlsx</div>
              <div className="gsheet-xlsx">
                <div className="gsheet-tabs">
                  <span className="on">DS30MS</span><span>DS50MS</span><span>DS70MS</span>
                </div>
                <pre>
                  <div className="gsheet-line hdr">APIR Code      Weight</div>
                  <div className="gsheet-line row">BGL0034AU      10.5</div>
                  <div className="gsheet-line row">SOL0001AU      8.0</div>
                  <div className="gsheet-line cash">AUD            1.5</div>
                </pre>
              </div>
              <div className="gsheet-note">a workbook tab per model — tab name = the model's portfolio code</div>
            </div>}
            rules={[
              <>The only Excel output. Sheet names cap at 31 characters (Excel's limit).</>,
            ]} />

          <PlatformCard name="CFS Edge" badge="consolidated"
            chips={[['folder', 'CFS/ (root)'], ['cash', 'omitted (1% plug)'], ['type', 'one Combined_Upload CSV for all models']]}
            mock={<FileMock fname="Combined_Upload_03092026.csv"
              lines={[
                ["Managed funds' SecurityCode must be in the format <APIRCode>.<CountryCode> …,,,", 'dim'],
                ['… 7 more fixed CFS instruction lines, then a blank ,,, row', 'dim'],
                ['ModelName,PortfolioCode,SecurityCode,TargetWeight', 'hdr'],
                ['Drummond Strategic 70 Portfolio,EDGDRUS70,BGL0034AU.AU,7.5', 'row'],
                ['Drummond Strategic 70 Portfolio,EDGDRUS70,QUS.XASX.AU,4.5', 'row'],
                ['Drummond Strategic 70 (Direct) Portfolio,EDGDRUD70,EDGDNRAEHC,6', 'row'],
              ]}
              note="every Edge model stacked in ONE file (Sep 2026) — no cash row (models total 99), no sell-out lines, CRLF line endings, filename DDMMYYYY" />}
            rules={[
              <>SecurityCode form: APIRs carry <code>.AU</code>, ASX-listed ETFs <code>.XASX.AU</code>, nested CFS SMAs (<code>EDG…</code>) stay bare. Any other key is emitted bare and flagged in the report detail.</>,
              <>The DNR sub-SMA reaches CFS as <code>EDGDNRAEHC</code> through the <strong>CFS</strong> unit-class mapping (<code>DNRHCSUB3 → EDGDNRAEHC</code>) — keep that mapping current.</>,
              <>A Combined_Upload is a full-model replacement, so sold-out funds are simply absent; the report detail lists them for manual instruction.</>,
            ]} />

          <PlatformCard name="CFS Accelerate" badge="consolidated"
            chips={[['folder', 'CFS - Accelerate/ (root)'], ['cash', 'omitted (2% plug)'], ['type', 'one Combined_Upload CSV for all models']]}
            mock={<FileMock fname="Combined_Upload_Accelerate_02092026.csv"
              lines={[
                ['… same fixed CFS preamble and header as CFS Edge', 'dim'],
                ['Drummond Balanced Portfolio,EDGDRUABAL,FSF3073AU.AU,15', 'row'],
                ['Drummond Balanced Portfolio,EDGDRUABAL,FSF4684AU.AU,10.5', 'row'],
                ['Drummond Growth Portfolio,EDGDRUAGRO,FSF3073AU.AU,15', 'row'],
              ]}
              note="identical layout to CFS Edge, separate file and folder — models total 98" />}
            rules={[
              <>Routed by the seeded sheet platform <code>CFS Accelerate</code> (like BT MDA). A CFS vehicle with no seeded sheet platform is reported <strong>unroutable</strong> — never guessed into the Edge file.</>,
            ]} />

          <PlatformCard name="CFS FirstChoice" badge="parked"
            chips={[['folder', '—'], ['cash', '—'], ['type', 'not generated']]}
            mock={<FileMock fname="(no file produced)"
              lines={[['Index Plus (CFSDIP…) vehicles are reported as skipped in every run', 'dim']]} />}
            rules={[
              <>Still instructed manually — its vehicles stay visible in the generation report so they're never silently forgotten.</>,
            ]} />
        </div>
        <p className="trading-muted">
          Codes, weights and names above are illustrative; the layouts, header text, cash tokens, filename
          patterns and rounding are exact (they replicate the current template set — July 2026 for the
          per-vehicle formats, September 2026 for the consolidated ones including CFS — the rendering lives in
          <code> trade_sheets.py</code>, one renderer per layout family).
        </p>
      </S>

      <S id="funddata" title="Registered instruments, management costs and rebates">
        <p>Two sidebar entries hold the global, fund-level data:</p>
        <ul>
          <li><strong>Registered instruments:</strong> codes registered by hand (sub-SMAs, cash tokens, ETFs);
            retire/reactivate — never deleted, so old lines always classify.</li>
          <li><strong>Management costs and rebates:</strong> one row per fund in the AP List table,
            plus any fund in a stored target or with a saved register row (marked "not on APL"). Search by APIR
            or fund name. The fee
            and rebate values (clear = revert the fee to the FE fee-table default) plus the rebate register:
            distributor, asset class, agreement and tailored status, agreement date, current volume, Drummond
            owner and notes. Register fields save on blur and never touch the fee values.</li>
          <li>Fee and rebate are the same global values edited on the strategy screen: either place writes the
            one stored row, so there is one source and two editors; the register's other fields are untouched
            by the strategy screen.</li>
          <li><strong>Held funds only</strong> collapses the register to funds in a current approved target or a
            live draft / pending set of any strategy, across all suites; the Held column shows how many of each
            and the tooltip names the strategies and suites. It is on by default each time the panel opens; untick it to see the whole AP List. <strong>Has rebate</strong> keeps only funds with a rebate value saved; the two filters and the search combine.</li>
        </ul>
      </S>

      <S id="report" title="Charts & tables (pasteable outputs)">
        <ul>
          <li><strong>Portfolio changes table:</strong> only the funds that moved, with asset-class band
            totals, one column per portfolio in the mix. Basis <em>Approved</em> compares the last approved
            version against the prior approved (the executed change — the same pair trade sheets use);
            <em> Draft vs approved</em> shows what a live draft would change. The Change column carries
            the net direction of each asset class across the portfolios (blank when mixed).
            <strong> ⧉ Copy table</strong> puts a formatted table on the clipboard — paste straight into
            Word or Outlook; <strong>⇓ PNG</strong> downloads any of the tables/charts as an image.</li>
          <li><strong>Asset class positioning:</strong> the Portfolio Dashboard's 5-bucket dot table
            (Strong Underweight → Strong Overweight) computed from THIS app's post-change weights —
            tilts vs the SAA averaged across the portfolios in the mix (DS 30/50/70/90 by default; add
            any others and their tilts join the average). Same rules: |tilt| &lt; 0.5% neutral, ±5%/±3%
            strong thresholds, zero-exposure = strong underweight, EM folds into International, plus the
            Total Growth vs Defensive summary row. Also copyable.</li>
          <li>The portfolio mix is remembered; ↺ DS defaults restores it. The SAA year selector re-points
            the positioning to another vintage.</li>
        </ul>
      </S>

      <S id="faq" title="Gotchas & FAQ">
        <ul>
          <li><strong>A sub shows lots of divergence right after changing the master?</strong> That's the point —
            it's the outstanding trade until platforms execute, not an error.</li>
          <li><strong>Legacy codes (CNDCP…, CFSMA-…) appear as unroutable in trade sheets:</strong> mapped vehicles
            that never went live; harmless, managed in Investment Config → Portfolio Mapping.</li>
          <li><strong>Strategic 20/40/60/80:</strong> registry placeholders that power other apps' SAAs — untick
            them in the Portfolios picker (remembered).</li>
          <li><strong>MER shows a value you never typed:</strong> that's the Manager Fee reference default;
            typing overrides it, clearing restores it.</li>
          <li><strong>Self-approval</strong> is a testing switch (<code>ALLOW_SELF_APPROVAL</code>) — turn it off
            before go-live so approver ≠ author is enforced.</li>
        </ul>
      </S>
    </div>
  );
}
